import { useState } from "react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BG = "https://images.pexels.com/photos/3137050/pexels-photo-3137050.jpeg";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Accesso effettuato");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:block lg:w-1/2 relative">
        <img src={BG} alt="Struttura architettonica" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-blue-950/70" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2.5">
            <div className="h-10 w-10 rounded-lg bg-white/15 backdrop-blur flex items-center justify-center">
              <Building2 className="h-5 w-5" />
            </div>
            <span className="font-heading font-extrabold text-lg">Contabilità Cantiere</span>
          </div>
          <div>
            <h1 className="font-heading text-4xl font-extrabold leading-tight tracking-tight">
              Traccia la contabilità<br />delle tue commesse.
            </h1>
            <p className="mt-4 text-blue-100/80 max-w-md leading-relaxed">
              Quantità, voci di capitolato e listini prezzi. Organizza tutto per cliente,
              commessa e tratta con KPI in tempo reale.
            </p>
          </div>
          <div className="text-xs text-blue-200/60">© 2026 Contabilità Cantiere</div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 bg-slate-50">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center gap-3">
            <img src="/powercom-accounting-emblem-white.png" alt="Emblema contabilità" className="h-11 w-11" data-testid="login-emblem" />
            <img src="/powercom-logo.webp" alt="POWERCOM" className="h-6 w-auto" data-testid="login-logo" />
          </div>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Accedi</span>
          <h2 className="font-heading text-4xl font-extrabold text-slate-900 tracking-tight mt-1">Bentornato.</h2>
          <p className="text-slate-500 mt-2 mb-8">Inserisci le tue credenziali per continuare.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs font-bold uppercase tracking-widest text-slate-500">Email</Label>
              <Input
                id="email" type="email" data-testid="login-email-input"
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@azienda.it" required className="mt-1.5 h-11"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-bold uppercase tracking-widest text-slate-500">Password</Label>
              <Input
                id="password" type="password" data-testid="login-password-input"
                value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" required className="mt-1.5 h-11"
              />
            </div>
            {error && (
              <div data-testid="login-error" className="text-sm text-rose-600 bg-rose-50 rounded-md px-3 py-2">
                {error}
              </div>
            )}
            <Button
              type="submit" disabled={loading} data-testid="login-submit-button"
              className="w-full bg-blue-700 hover:bg-blue-800 h-11 active:scale-[0.98] transition-transform"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Accedi"}
            </Button>
          </form>

          <p className="text-sm text-slate-500 mt-6 text-center">
            L'accesso è riservato agli utenti abilitati dall'amministratore.
          </p>
        </div>
      </div>
    </div>
  );
}
